import React, { useState } from 'react';
import { 
  Truck, Package, Globe, Clock, Shield, 
  MapPin, Phone, Mail, Menu, X, ArrowRight, MessageCircle 
} from 'lucide-react';
import './App.css';

// --- COMPONENTS ---

const Navbar = ({ toggleMenu, isMenuOpen }) => (
  <nav className="navbar">
    <div className="container nav-container">
      <div className="logo">
        <Truck className="logo-icon" size={32} />
        <h1>SharpWay<span>Logistics</span></h1>
      </div>
      <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
        <a href="#home" onClick={toggleMenu}>Home</a>
        <a href="#about" onClick={toggleMenu}>About</a>
        <a href="#services" onClick={toggleMenu}>Services</a>
        <a href="#coverage" onClick={toggleMenu}>Coverage</a>
        <a href="#contact" onClick={toggleMenu} className="btn-nav">Get Quote</a>
      </div>
      <div className="menu-icon" onClick={toggleMenu}>
        {isMenuOpen ? <X /> : <Menu />}
      </div>
    </div>
  </nav>
);

const Hero = () => (
  <header id="home" className="hero">
    <div className="overlay"></div>
    <div className="container hero-content">
      <span className="badge">Welcome to Sharpway</span>
      <h1>Your Delivery, <br />Our <span className="highlight">Priority</span></h1>
      <p>Fast, secure, and reliable logistics solutions bridging the gap between senders and receivers across Nigeria and the world.</p>
      <div className="hero-btns">
        <a href="#services" className="btn btn-primary">Our Services</a>
        <a href="#contact" className="btn btn-outline">Track Shipment</a>
      </div>
    </div>
  </header>
);

const About = () => (
  <section id="about" className="section bg-light">
    <div className="container about-grid">
      <div className="about-text">
        <h2>Who We Are</h2>
        <p><strong>Sharpway Logistics Ltd</strong> is a modern, fast-growing logistics, courier, and supply-chain support company based in Lagos, Nigeria. We simplify moving goods for businesses, online vendors, and individuals.</p>
        
        <div className="values-list">
          <div className="value-item">
            <Clock className="icon-sm" /> 
            <div><strong>Speed:</strong> Prompt delivery without compromising safety.</div>
          </div>
          <div className="value-item">
            <Shield className="icon-sm" /> 
            <div><strong>Integrity:</strong> Honesty and transparency in every mile.</div>
          </div>
        </div>
      </div>
      <div className="mission-cards">
        <div className="card">
          <h3>Our Mission</h3>
          <p>To provide secure, fast, and customer-focused logistics services that simplify moving goods across short and long distances.</p>
        </div>
        <div className="card highlight-card">
          <h3>Our Vision</h3>
          <p>To become one of Nigeria’s and beyond most trusted logistics brands, known for reliability and innovation.</p>
        </div>
      </div>
    </div>
  </section>
);

const Services = () => {
  const services = [
    { title: "Dispatch & Courier", desc: "Same-day and next-day delivery within Nigeria.", icon: <Truck /> },
    { title: "E-commerce Support", desc: "Tailored delivery support for vendors, SMEs, and retailers.", icon: <Globe /> },
    { title: "Corporate Deliveries", desc: "Document and office item movement for institutions.", icon: <Package /> },
    { title: "Inter-State Logistics", desc: "Safe movement from Lagos to other states and across the globe.", icon: <MapPin /> },
    { title: "Special Handling", desc: "Fragile, high-value, and sensitive deliveries.", icon: <Shield /> },
    { title: "Art Services", desc: "Specialized handling for murals, sculptures, and frames.", icon: <Package /> },
  ];

  return (
    <section id="services" className="section">
      <div className="container">
        <div className="section-header">
          <h2>Our Services</h2>
          <p>Comprehensive logistics solutions tailored to your needs.</p>
        </div>
        <div className="services-grid">
          {services.map((s, index) => (
            <div key={index} className="service-card">
              <div className="service-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Coverage = () => (
  <section id="coverage" className="section bg-dark text-white">
    <div className="container">
      <h2>Global & Local Coverage</h2>
      <div className="coverage-grid">
        <div className="cov-item">
          <h3>Primary</h3>
          <p>Nigeria Metropolis (Lagos,35 states)</p>
        </div>
        <div className="cov-item">
          <h3>Regional</h3>
          <p>Ghana & West Africa</p>
        </div>
        <div className="cov-item">
          <h3>International</h3>
          <p>USA, UK, Canada, Russia</p>
        </div>
      </div>
    </div>
  </section>
);

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to WhatsApp with form data
    const text = `Hello Sharpway, my name is ${formData.name}. Phone: ${formData.phone}. Message: ${formData.message}`;
    window.open(`https://wa.me/234123456789?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <section id="contact" className="section">
      <div className="container contact-wrapper">
        <div className="contact-info">
          <h2>Get In Touch</h2>
          <p>Ready to ship? Contact us today.</p>
          <div className="info-item"><MapPin /> 14, Ejigbo, Lagos, Nigeria</div>
          <div className="info-item"><Phone /> +234 123 456 789</div>
          <div className="info-item"><Mail /> sharpwaylogistics@gmail.com</div>
        </div>
        <form className="contact-form" onSubmit={handleSubmit}>
          <h3>Send a Request</h3>
          <input 
            type="text" placeholder="Your Name" required 
            onChange={(e) => setFormData({...formData, name: e.target.value})}
          />
          <input 
            type="tel" placeholder="Phone Number" required 
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
          />
          <textarea 
            placeholder="What do you want to ship?" rows="4" required
            onChange={(e) => setFormData({...formData, message: e.target.value})}
          ></textarea>
          <button type="submit" className="btn btn-primary">Send via WhatsApp <ArrowRight size={16} /></button>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer>
    <div className="container footer-content">
      <p>&copy; {new Date().getFullYear()} Sharpway Logistics Ltd. All rights reserved.</p>
      <p>Your Delivery, Our Priority</p>
    </div>
  </footer>
);

const WhatsAppFloat = () => (
  <a 
    href="https://wa.me/234123456789" 
    className="whatsapp-float" 
    target="_blank" 
    rel="noopener noreferrer"
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle size={32} />
  </a>
);

// --- MAIN APP COMPONENT ---

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="App">
      <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
      <Hero />
      <About />
      <Services />
      <Coverage />
      <Contact />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;